import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './WarningModal.styles';
import { Modal } from '@/components/UI/Modal';
import { Button } from '@/components/UI/Button/Button';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { CommonTestIds } from '@/__e2e__/testIds/common';

export interface WarningModalProps {
  isOpen: boolean;
  onClose: () => void | Promise<void>;
  title: string;
  icon?: string;
  iconSize?: number;
  iconWidth?: number;
  iconHeight?: number;
  hasIconBg?: boolean;
  actions?: React.ReactNode;
  description: string | React.ReactNode;
  buttonText?: string;
  dataTestId?: string;
}

export const WarningModal: React.FC<WarningModalProps> = ({
  isOpen,
  onClose,
  title,
  icon,
  buttonText,
  dataTestId,
  description,
  actions,
  iconSize = 35,
  hasIconBg = false,
  iconWidth,
  iconHeight,
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();

  return (
    <Modal
      isInPortal
      dataTestId={dataTestId}
      isOpen={isOpen}
      onClose={onClose}
      maxWidth={isDesktop ? 445 : 345}
    >
      <S.Content>
        {icon && (
          <S.IconRow>
            <S.IconBgWrapper $hasBg={hasIconBg}>
              <IconBox
                $filename={icon}
                $width={iconWidth ?? iconSize}
                $height={iconHeight ?? iconSize}
                $iconWidth={iconWidth ?? iconSize}
                $iconHeight={iconHeight ?? iconSize}
              />
            </S.IconBgWrapper>
          </S.IconRow>
        )}
        <S.Title data-testid={CommonTestIds.warningModal.title}>
          {title}
        </S.Title>
        <S.Description data-testid={CommonTestIds.warningModal.description}>
          {description}
        </S.Description>
        {actions ? (
          <S.ActionsContainer data-testid={CommonTestIds.warningModal.actions}>
            {actions}
          </S.ActionsContainer>
        ) : (
          <Button
            data-testid={CommonTestIds.warningModal.actionButton}
            appearance="secondary"
            isFullWidth
            borderRadius={14}
            onClick={onClose}
          >
            {buttonText ?? t('common.got_it')}
          </Button>
        )}
      </S.Content>
    </Modal>
  );
};
